// Data hook for TabularScopePreview — owns the Parquet fetch so the component
// stays free of `@/services/api` value-imports (frontend purity rule).
//
// The tabular import selection was built against the lake document's Parquet
// (not the raw bytes), so we read that same Parquet via `getTabularPreview`,
// pulling enough leading rows to cover the deepest selected `__row_index`.

import { useEffect, useState } from 'react'
import { lakeApi, type TabularColumn } from '@/services/api/lake'

// Cap how many leading Parquet rows we pull when a selection reaches deep into
// a large file. The common case (a handful of rows near the top) stays tiny.
const MAX_FETCH_ROWS = 50_000

type Cell = string | number | boolean | null

export interface TabularScopePreviewData {
  columns: TabularColumn[]
  rows: Cell[][]
}

export function useTabularScopePreview(lakeDocumentId: string, selectedRows: number[] | null) {
  const [data, setData] = useState<TabularScopePreviewData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Serialize so the effect only re-runs when the row selection's value changes.
  const selRowsKey = JSON.stringify(selectedRows ?? null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setData(null)

    const rowsSel = JSON.parse(selRowsKey) as number[] | null
    const maxIdx = rowsSel?.length ? rowsSel.reduce((m, r) => Math.max(m, r), 0) : 0
    const limit = Math.min(maxIdx + 1, MAX_FETCH_ROWS)

    lakeApi
      .getTabularPreview(lakeDocumentId, 0, Math.max(limit, 1))
      .then(resp => {
        if (!cancelled) setData({ columns: resp.columns, rows: resp.rows })
      })
      .catch(err => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load preview')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [lakeDocumentId, selRowsKey])

  return { data, loading, error }
}
